import { useMemo, useState } from 'react'
import { cn, kstDateStr } from '@biznomi/ui-react'

/**
 * 여러 날을 골라 쉬는 달력.
 *
 * 왜 기간(시작~종료)이 아니라 날짜 선택인가
 *   기간으로 받으면 "그 사이 주말이 근무일인가" 를 우리가 판정해야 한다.
 *   토요일 격주 근무·교대제처럼 회사마다 달라 맞출 수 없고, 공휴일까지 가면 법 해석이다.
 *   직원이 쉴 날만 고르면 그 문제가 통째로 사라진다 — 안 고르면 그만이다 (§1 경계).
 *
 * 왜 커스텀인가
 *   input[type=date] 는 하나만 고를 수 있다. 여러 날은 표현이 안 된다.
 *   (하루짜리인 수기 등록 화면은 지금도 네이티브 input 을 쓴다 — iOS 함정 회피)
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

function ymd(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

export function MultiDatePicker({
  value,
  onChange,
  max,
  disabled,
}: {
  /** 고른 날짜들 (YYYY-MM-DD) */
  value: string[]
  onChange: (next: string[]) => void
  /** 최대 몇 개까지. 반차처럼 하루만 받을 때 1 */
  max?: number
  disabled?: boolean
}) {
  const today = kstDateStr()
  const [cursor, setCursor] = useState(() => {
    const [y, m] = today.split('-').map(Number)
    return { y, m: m - 1 }
  })

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1)
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate()
    const cells: (number | null)[] = Array(first.getDay()).fill(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(d)
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }, [cursor])

  const selected = new Set(value)

  function toggle(d: number) {
    if (disabled) return
    const key = ymd(cursor.y, cursor.m, d)
    if (selected.has(key)) {
      onChange(value.filter((v) => v !== key))
      return
    }
    // max 가 1 이면 갈아치운다 — 반차는 하루뿐이라 "고친다" 가 자연스럽다
    if (max === 1) { onChange([key]); return }
    if (max && value.length >= max) return
    onChange([...value, key].sort())
  }

  function move(delta: number) {
    setCursor((c) => {
      const m = c.m + delta
      if (m < 0) return { y: c.y - 1, m: 11 }
      if (m > 11) return { y: c.y + 1, m: 0 }
      return { y: c.y, m }
    })
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button" onClick={() => move(-1)} aria-label="이전 달"
          className="rounded-md px-3 py-1.5 text-lg leading-none text-slate-500 hover:bg-slate-100"
        >‹</button>
        <span className="font-semibold">{cursor.y}년 {cursor.m + 1}월</span>
        <button
          type="button" onClick={() => move(1)} aria-label="다음 달"
          className="rounded-md px-3 py-1.5 text-lg leading-none text-slate-500 hover:bg-slate-100"
        >›</button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, i) => (
          <div
            key={w}
            className={cn(
              'py-1 text-xs font-medium',
              i === 0 ? 'text-[var(--color-danger)]' : i === 6 ? 'text-primary' : 'text-slate-400',
            )}
          >
            {w}
          </div>
        ))}

        {grid.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />
          const key = ymd(cursor.y, cursor.m, d)
          const on = selected.has(key)
          const isToday = key === today
          const dow = i % 7
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(d)}
              disabled={disabled}
              aria-pressed={on}
              /* 터치 영역 44px 이상 — 손가락으로 누르는 화면이다 */
              className={cn(
                'flex h-11 items-center justify-center rounded-md text-sm tabular-nums transition-colors',
                on
                  ? 'bg-primary font-bold text-white'
                  : dow === 0
                    ? 'text-[var(--color-danger)] hover:bg-slate-100'
                    : dow === 6
                      ? 'text-primary hover:bg-slate-100'
                      : 'text-slate-700 hover:bg-slate-100',
                !on && isToday && 'ring-1 ring-primary',
                disabled && 'opacity-40',
              )}
            >
              {d}
            </button>
          )
        })}
      </div>
    </div>
  )
}
